import React, { useState, useRef, useEffect } from "react"
import { useLanguage } from "../../contexts/LanguageContext"
import locales from "../../locales.json"
import InfoPopover from "../infoPopover/infoPopover"
import LanguageFlag from "../language/LanguageFlag"

/**
 * Dropdown selector for multiple language translation options.
 * Manages "All" selection logic and provides localized language names.
 * @param {Object} props
 * @param {Function} props.onLanguagesChange - Callback for selected languages array
 * @returns {JSX.Element}
 */
const MultiLanguageSelector = ({ onLanguagesChange }) => {
  const { currentLanguage, t } = useLanguage()
  const [selectedLanguages, setSelectedLanguages] = useState([])
  const [isExpanded, setIsExpanded] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    if (!isExpanded) return

    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsExpanded(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isExpanded])

  useEffect(() => {
    if (!onLanguagesChange) return

    const availableLanguages = locales.languages.filter(lang => lang.code !== currentLanguage)
    const allSelected = availableLanguages.length > 0 && selectedLanguages.length === availableLanguages.length

    if (allSelected) {
      onLanguagesChange(["All"])
    } else {
      onLanguagesChange(selectedLanguages)
    }
  }, [selectedLanguages, currentLanguage, onLanguagesChange])

  /**
   * Gets localized language name based on current language context.
   * @param {string} languageCode
   * @returns {string} Localized language name
   */
  const getLocalizedLanguageName = languageCode => {
    if (locales.languageNames && locales.languageNames[currentLanguage]) {
      return locales.languageNames[currentLanguage][languageCode] || languageCode
    }
    const lang = locales.languages.find(l => l.code === languageCode)
    return lang ? lang.label : languageCode
  }

  /**
   * Generates display text for current selection state.
   * @param {Array} selectedLanguages
   * @returns {string} Display text
   */
  const getSelectionDisplayText = selectedLanguages => {
    if (selectedLanguages.length === 0) {
      return typeof t === "function" ? t("Text.textMultiLanguageNoSelection") : "Text.textMultiLanguageNoSelection"
    }
    return `${selectedLanguages.length} ${typeof t === "function" ? t("Text.textMultiLanguageSelectionCount") : "Text.textMultiLanguageSelectionCount"}`
  }

  /**
   * Toggles a language in the selection list.
   * @param {string} languageCode
   */
  const handleLanguageToggle = languageCode => {
    setSelectedLanguages(prev => {
      if (prev.includes(languageCode)) {
        return prev.filter(lang => lang !== languageCode)
      } else {
        return [...prev, languageCode]
      }
    })
  }

  const handleSelectAll = () => {
    const availableLanguages = locales.languages.filter(lang => lang.code !== currentLanguage)
    const allSelected = availableLanguages.length > 0 && selectedLanguages.length === availableLanguages.length

    if (allSelected) {
      setSelectedLanguages([])
    } else {
      setSelectedLanguages(availableLanguages.map(lang => lang.code))
    }
  }

  return (
    <div className="row multiLanguageSelector">
      <div className="col-5 d-flex justify-content-end align-items-center">
        <span>{typeof t === "function" ? t("Text.textMultiLanguageTitle") : "Text.textMultiLanguageTitle"}</span>
        <InfoPopover
          element={{
            alert: typeof t === "function" ? t("Text.textMultiLanguageInfo") : "Text.textMultiLanguageInfo",
          }}
        />
      </div>
      <div className="col">
        <div className="language-selector-container" ref={containerRef}>
          <div className="language-selector-header" onClick={() => setIsExpanded(!isExpanded)}>
            <span>{getSelectionDisplayText(selectedLanguages)}</span>
            <span className={`listToggleIcon${isExpanded ? " open" : ""}`} />
          </div>

          {isExpanded && (
            <div className="language-selector-dropdown">
              <div className="language-option select-all-option">
                <label className="language-checkbox">
                  <input
                    type="checkbox"
                    checked={
                      locales.languages.filter(lang => lang.code !== currentLanguage).length > 0 && selectedLanguages.length === locales.languages.filter(lang => lang.code !== currentLanguage).length
                    }
                    onChange={handleSelectAll}
                  />
                  <span className="checkmark"></span>
                  <b>{t("Text.textMultiLanguageSelectAll")}</b>
                </label>
              </div>
              {locales.languages
                .filter(lang => lang.code !== currentLanguage)
                .map(lang => (
                  <div key={lang.code} className="language-option">
                    <label className="language-checkbox">
                      <input type="checkbox" checked={selectedLanguages.includes(lang.code)} onChange={() => handleLanguageToggle(lang.code)} />
                      <span className="checkmark"></span>
                      <LanguageFlag languageCode={lang.code} size={32} />
                      <span className="language-name">{getLocalizedLanguageName(lang.code)}</span>
                    </label>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MultiLanguageSelector
